import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[100] bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

/**
 * Universal "unsaved-changes" guard built into DialogContent.
 *
 * Behaviour (all opt-out by passing `disableDirtyGuard`):
 *   • Any user-initiated `input` or `change` event inside the dialog flips a
 *     ref-tracked "userTouched" flag to true. Programmatic React renders
 *     never count (we filter on `event.isTrusted`).
 *   • On Esc, outside-click, OR the built-in X button click, if the user
 *     has touched any field we trigger `window.confirm(message)`. Cancel
 *     keeps the dialog open with edits intact; OK lets it close.
 *   • Saving programmatically (e.g. `setOpen(false)` after a successful
 *     PUT) is unaffected — the guard only intercepts user dismiss paths.
 *   • Existing per-modal `onEscapeKeyDown` / `onPointerDownOutside`
 *     handlers still run first; if they call `e.preventDefault()`, the
 *     guard is skipped so the modal honours their custom intent.
 *
 * Props:
 *   disableDirtyGuard?: boolean        — opt-out for non-editing dialogs
 *   dirtyGuardMessage?: string         — custom confirm copy
 *   dirtyGuardActive?: () => boolean   — caller-provided override for the
 *                                        dirty signal (useful when state
 *                                        lives in React, not the DOM).
 */
const DEFAULT_GUARD_MSG = "You have unsaved changes. Closing will discard them.";

const DialogContent = React.forwardRef(({
  className,
  children,
  disableDirtyGuard = false,
  dirtyGuardMessage,
  dirtyGuardActive,
  onEscapeKeyDown,
  onPointerDownOutside,
  ...props
}, ref) => {
  const contentRef = React.useRef(null);
  const userTouched = React.useRef(false);
  // Hidden Radix Close button — used to programmatically dismiss the
  // dialog once the user has confirmed via the branded AlertDialog.
  const closeBtnRef = React.useRef(null);

  // Wire up listeners on every editable element inside the dialog. We
  // attach in capture mode so we still hear events even when a consumer
  // calls `stopPropagation()` somewhere downstream.
  React.useEffect(() => {
    if (disableDirtyGuard) return undefined;
    userTouched.current = false;
    const node = contentRef.current;
    if (!node) return undefined;
    const onAny = (e) => {
      if (!e.isTrusted) return;
      const t = e.target;
      if (t && (t.readOnly || t.disabled)) return;
      userTouched.current = true;
    };
    node.addEventListener("input", onAny, true);
    node.addEventListener("change", onAny, true);
    return () => {
      node.removeEventListener("input", onAny, true);
      node.removeEventListener("change", onAny, true);
    };
  }, [disableDirtyGuard]);

  const isDirty = React.useCallback(() => {
    if (disableDirtyGuard) return false;
    if (typeof dirtyGuardActive === "function") {
      try { return !!dirtyGuardActive(); } catch { /* swallow */ }
    }
    return userTouched.current;
  }, [disableDirtyGuard, dirtyGuardActive]);

  // Async branded confirm. We always preventDefault first (synchronously)
  // so Radix doesn't close the dialog out from under us, then await the
  // user's choice and either let the dialog stay open OR programmatically
  // dismiss it via the hidden close button.
  const guardAndPrompt = (e) => {
    if (!isDirty()) return;
    e.preventDefault();
    brandedConfirm({
      title: "Discard unsaved changes?",
      description: dirtyGuardMessage || DEFAULT_GUARD_MSG,
      confirmText: "Discard",
      cancelText: "Keep editing",
      tone: "danger",
    }).then((ok) => {
      if (ok) {
        userTouched.current = false;
        // Click the hidden Radix Close to fire the standard close flow
        // (onOpenChange(false) on the parent <Dialog>, animations, etc.).
        closeBtnRef.current?.click();
      }
    });
  };

  // Merge our internal ref with whatever ref the caller supplied.
  const mergedRef = React.useCallback((node) => {
    contentRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  const handleEsc = (e) => {
    if (onEscapeKeyDown) onEscapeKeyDown(e);
    if (!e.defaultPrevented) guardAndPrompt(e);
  };
  const handleOutside = (e) => {
    if (onPointerDownOutside) onPointerDownOutside(e);
    if (!e.defaultPrevented) guardAndPrompt(e);
  };
  // The built-in X button doesn't route through onEscapeKeyDown or
  // onPointerDownOutside — it calls Radix's internal close directly. We
  // intercept its click and gate it behind the same branded prompt.
  const handleXClick = (e) => {
    if (!isDirty()) return;
    e.preventDefault();
    e.stopPropagation();
    brandedConfirm({
      title: "Discard unsaved changes?",
      description: dirtyGuardMessage || DEFAULT_GUARD_MSG,
      confirmText: "Discard",
      cancelText: "Keep editing",
      tone: "danger",
    }).then((ok) => {
      if (ok) {
        userTouched.current = false;
        closeBtnRef.current?.click();
      }
    });
  };

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={mergedRef}
        className={cn(
          "fixed left-[50%] top-[50%] z-[100] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        onEscapeKeyDown={handleEsc}
        onPointerDownOutside={handleOutside}
        {...props}>
        {children}
        {/* Hidden close used to programmatically dismiss after the user
            confirms the branded "Discard?" prompt. */}
        <DialogPrimitive.Close ref={closeBtnRef} className="hidden" aria-hidden="true" />
        <DialogPrimitive.Close
          onClick={handleXClick}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
